/**
 * Form Builder API
 *
 * API client functions for managing form definitions.
 */

import { apiClient } from '@/lib/api';
import type { FormDefinition } from '../components/form-builder/types';

export interface FormListItem {
  id: number;
  name: string;
  description: string;
  created_time: string;
  updated_time: string;
  form_type_name?: string;
  is_system: boolean;
}

export interface FormListResponse {
  values: FormListItem[];
}

export interface FormDetailResponse {
  values: FormDefinition;
}

export interface FormConfigResponse {
  values: Array<{
    id: number;
    name: string;
    label: string;
    description?: string;
    option?: unknown;
    validations?: unknown;
  }>;
}

export interface FormTypeResponse {
  values: Array<{ id: number; name: string }>;
}

/** Row of skewb_works.form_auto_populate_mapping, as served to the device (CWL-303). */
export interface FormAutoPopulateMapping {
  mapping_key: string;
  field_key: string;
  fixed_value: string | null;
}

export interface FormAutoPopulateMappingsResponse {
  values: FormAutoPopulateMapping[];
}

export async function getFormList(): Promise<FormListResponse> {
  return apiClient.get<FormListResponse>('/form');
}

export async function getFormById(id: number): Promise<FormDetailResponse> {
  return apiClient.get<FormDetailResponse>(`/form/${id}`);
}

export async function createForm(data: FormDefinition): Promise<{ values: number }> {
  return apiClient.post<{ values: number }>('/form/add', data);
}

export async function updateForm(id: number, data: FormDefinition): Promise<void> {
  return apiClient.put<void>(`/form/${id}`, data);
}

export async function deleteForm(id: number): Promise<void> {
  return apiClient.delete<void>(`/form/${id}`);
}

export async function getControlConfigs(): Promise<FormConfigResponse> {
  return apiClient.get<FormConfigResponse>('/form/configdata');
}

export async function getFormTypes(): Promise<FormTypeResponse> {
  return apiClient.get<FormTypeResponse>('/form/type/list');
}

/** Active auto-populate aliases — the builder flags fields SAP may fill (CWL-470). */
export async function getFormAutoPopulateMappings(): Promise<FormAutoPopulateMappingsResponse> {
  return apiClient.get<FormAutoPopulateMappingsResponse>('/mobile/form-auto-populate-mappings');
}
